import {
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  GridApi,
} from 'ag-grid-community';
import { Injectable } from '@angular/core';
import {
  AnalystRoleEnum,
  ResultTableColumn,
  ResultTableColumns,
  User,
} from '@compumark/analyst-selection-tool-client';
import { UserPreferencesService } from '@compumark/user-preferences-backend-specifications';
import * as _ from 'lodash';
import { first } from 'rxjs';
import { UserService } from '../../security/services/user.service';

@Injectable()
export class TableColumnService {
  public static readonly columnsOpenPanel = {
    id: 'columns',
    labelDefault: 'Columns',
    labelKey: 'columns',
    iconKey: 'columns',
    toolPanel: 'agColumnsToolPanel',
    minWidth: 225,
    maxWidth: 225,
    width: 225,
    toolPanelParams: {
      suppressRowGroups: true,
      suppressValues: true,
      suppressPivots: true,
      suppressPivotMode: true,
    },
  };

  currentPreferenceName!: keyof ResultTableColumns;
  columnStates: ResultTableColumns = {
    taskOverviewTableColumns: [],
  };

  debouncedColumnChange = _.debounce(
    (
      event:
        | ColumnMovedEvent
        | ColumnVisibleEvent
        | ColumnResizedEvent
        | ColumnPinnedEvent,
    ) => {
      this.userService
        .getCurrentUserObservable()
        .pipe(first())
        .subscribe((user) => {
          const currentPreferences = user?.preference?.preference || {};

          this.columnStates[this.currentPreferenceName] = event.api
            .getColumnState()
            .map((cs) => ({
              colId: cs.colId,
              hide: cs.hide,
              width: cs.width,
              pinned: cs.pinned,
            })) as ResultTableColumn[];

          const updatedPreferences = {
            ...currentPreferences,
            [this.currentPreferenceName]: this.columnStates[
              this.currentPreferenceName
            ]?.length
              ? this.columnStates[this.currentPreferenceName]
              : currentPreferences[this.currentPreferenceName] ?? [],
          };

          this.userPreferenceService
            .saveUserPreferences(updatedPreferences as any)
            .pipe(first())
            .subscribe();
        });
    },
    300,
  );

  constructor(
    private userPreferenceService: UserPreferencesService,
    private userService: UserService,
  ) {}

  initColumns(
    gridApi: GridApi,
    user: User,
    preferenceName: keyof ResultTableColumns,
  ): void {
    this.currentPreferenceName = preferenceName;

    const userPreference = user?.preference?.preference;
    gridApi.setColumnsVisible(['indexing'], this.userContainsQARole(user));

    const userPreferenceColumns = _.get(userPreference, [preferenceName], []);

    const columnState = gridApi.getColumnState();

    const syncCol = columnState.find(
      (c) => c.colId === 'synchronised' && !c.hide,
    );
    const index = userPreferenceColumns.findIndex(
      (c) => c.colId === 'synchronised',
    );

    if (index !== -1) {
      userPreferenceColumns[index].hide = !syncCol;
    }

    if (userPreferenceColumns.length) {
      const sortedColumnState = this.getSortedColumnStateArray(
        userPreferenceColumns,
        columnState,
      );

      gridApi.applyColumnState({
        state: sortedColumnState,
        applyOrder: true,
      });
    }
  }

  onColumnChange(
    event:
      | ColumnMovedEvent
      | ColumnVisibleEvent
      | ColumnResizedEvent
      | ColumnPinnedEvent,
  ): void {
    this.debouncedColumnChange(event);
  }

  private getSortedColumnStateArray(
    userPreferenceColumns: ResultTableColumn[],
    columnState: ColumnState[],
  ): ColumnState[] {
    const sortedArray: ColumnState[] = [];
    for (const up of userPreferenceColumns) {
      const col = columnState.find((c) => c.colId === up.colId);
      if (!!col) {
        col!.hide = up.hide;
        col!.width = up.width;
        sortedArray.push(col!);
        col!.pinned = up.pinned as any;
      }
    }
    return sortedArray;
  }

  private userContainsQARole(user: User): boolean {
    return user.roles?.includes(AnalystRoleEnum.QUALITY_ANALYST) ?? false;
  }
}
